import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  FeatureFlag,
  NumberFeatureFlag,
  useFeatureFlagNumber,
  useFeatureFlagValue,
} from "../../feature-flags";
import { clampPageSize, PAY_COMPONENT_IMPORT_BASE_PATH } from "../config";
import { useImportPayComponents } from "../hooks";
import { usePayComponentImportRedesignEnabled } from "../hooks/usePayComponentImportRedesignEnabled";
import { useCompanyProvisionAgentId } from "../../onboarding/hooks/useCompanyProvisionAgentId";
import { useWorkflowCommit } from "../hooks/useWorkflowCommit";
import { usePayComponentImportWorkloadsManager } from "../hooks/usePayComponentImportWorkloadsManager";
import {
  INITIAL_WIZARD_STATE,
  newImportWizardState,
  type WizardState,
} from "../types";
import { csvTextToFile } from "../utils/csvTextToFile";
import { buildImportRows } from "../utils/buildImportRows";
import { buildFailedRowsCsv } from "../utils/resultsExport";
import { rowsToJsonImportFile } from "../../onboarding/utils/rowsToJsonImportFile";
import {
  buildWizardSessionRecord,
  clearWizardSession,
  loadWizardSession,
  tryRestoreWizardState,
  saveWizardSession,
} from "../utils/wizardSession";
import { WizardContext, type CommitImportArgs } from "./WizardContext";
import { WizardRouteGuard } from "./WizardRouteGuard";
import type { MyPayEnvironment } from "../../generated/core-api";

export interface WizardProviderProps {
  /** Notifies the layout when validate/commit work is in flight (redesign gate lock). */
  onBusyChange?: (busy: boolean) => void;
  /**
   * The Christmas API company code used for all import/mapping endpoints.
   * Provided by `PayComponentImportLayout` which derives it from the
   * validated company + environment selection gate.
   */
  companyCode: string;
  /**
   * Display name from the company gate — persisted with the wizard session so a
   * refresh can restore company context without re-validation.
   */
  companyName: string;
  /**
   * The Datapay environment chosen in the selection gate. Seeds
   * `WizardState.mypayEnvironment` so it is chosen once (in the gate) and read
   * from wizard state everywhere downstream (import dry-run, discovery, commit).
   */
  initialMypayEnvironment?: MyPayEnvironment | null;
}

/**
 * Provides wizard state and the `runImport` action to all screens under
 * `/onboarding/pay-component-import`. Renders the current route via `<Outlet />`.
 *
 * Must be mounted inside a React Router route context.
 *
 * Step 13 (LADR-039 / #440): `FeatureFlag.EnableWorkloadsCutover` switches which
 * hook backs the *commit* path — Track 1 (`useWorkflowCommit`, `invokeAgent`
 * Temporal workflow) when off, Track 2 (`usePayComponentImportWorkloadsManager`,
 * Slice 5 workloads REST APIs) when on. `runImport` (the Christmas dry-run
 * preflight, W3c) and everything else below it (session resume/persist,
 * redesign-mode state shape, LaunchDarkly page-size seeding, the
 * `invalidateValidation`/`reportUiBusy`/`discardUnsavedProgress` affordances)
 * is unaffected by the flag and stays identical on both tracks — it lives in
 * the shared `useWizardProviderState` below.
 *
 * Mirrors the Step 9 precedent in
 * `src/work-patterns/components/WorkPatternImport/WorkPatternImportInput.tsx`:
 * a plain `flag ? useHookA() : useHookB()` inside one component would
 * conditionally call a different hook depending on a runtime value — a
 * Rules-of-Hooks violation (and would fire both managers' queries/mutations if
 * both were called unconditionally in the same component). Instead, this
 * top-level component only ever calls `useFeatureFlagValue` and renders one of
 * two child components, each of which unconditionally calls exactly one commit
 * hook. Only the active track's component instance mounts, so only its hook
 * (and therefore its API calls) ever fires.
 *
 * Unlike work-patterns' two manager hooks (which share one
 * `WorkPatternImportManagerReturn` interface, so the presentational body needs
 * no branching), `useWorkflowCommit`'s `commit(): Promise<ImportResult>` and
 * `usePayComponentImportWorkloadsManager`'s fire-and-forget `commit(args): void`
 * are shaped too differently to hide behind one shared interface. So instead
 * each track component below builds its own `commitImport` closure against the
 * *existing* `WizardContextValue` contract (`commitImport` / `commitProgress` /
 * `isCommitting` / `commitError`) — Track 2 bridges its reactive `result` /
 * `isCommitting` / `managerError` state onto that contract with a `useEffect`
 * (there is no promise to await). Track 2 always reports `commitProgress:
 * null`; `ImportResultsScreen` already null-tolerates that (falls back to a
 * generic "Submitting rows…" message instead of the per-chunk progress bar),
 * so `WizardContextValue`'s shape needed no extension.
 */
export function WizardProvider(props: WizardProviderProps) {
  const useWorkloadsCutover = useFeatureFlagValue(FeatureFlag.EnableWorkloadsCutover);

  return useWorkloadsCutover ? (
    <WizardProviderWorkloads {...props} />
  ) : (
    <WizardProviderTrackOne {...props} />
  );
}

/**
 * Shared non-commit wizard state + effects: the current `WizardState`
 * (redesign-aware session restore), the Christmas dry-run preflight
 * (`runImport`, W3c), LaunchDarkly page-size seeding, session persist, and
 * the `invalidateValidation`/`reportUiBusy`/`discardUnsavedProgress`
 * affordances. Both tracks call this unconditionally (same hook call order
 * every render) and layer their own commit-hook selection on top of the
 * result — `isCommitting` is a parameter (not read here) precisely because
 * it comes from whichever commit hook the calling track mounted.
 */
function useWizardProviderState(
  {
    companyCode,
    companyName,
    initialMypayEnvironment = null,
    onBusyChange,
  }: WizardProviderProps,
  isCommitting: boolean,
) {
  const redesignEnabled = usePayComponentImportRedesignEnabled();
  // Redesign matches Work Patterns: no session resume on refresh. Legacy keeps
  // #228 resume. Clear any stale session once when redesign is on.
  const [state, setState] = useState<WizardState>(() => {
    if (redesignEnabled) {
      clearWizardSession();
      return {
        ...INITIAL_WIZARD_STATE,
        mypayEnvironment: initialMypayEnvironment,
      };
    }
    return tryRestoreWizardState(companyCode, initialMypayEnvironment);
  });
  const navigate = useNavigate();
  const location = useLocation();
  const restoredRouteRef = useRef(false);
  const importRequestTokenRef = useRef(0);
  const mappingRoute = redesignEnabled
    ? PAY_COMPONENT_IMPORT_BASE_PATH
    : `${PAY_COMPONENT_IMPORT_BASE_PATH}/map`;

  // Set up the mutation once at the provider level so runImport can call
  // mutateAsync without conditionally invoking a hook.
  const { mutateAsync, isPending: isValidating } =
    useImportPayComponents(companyCode);

  const [uiBusy, setUiBusy] = useState(false);

  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  useEffect(() => {
    onBusyChangeRef.current?.(isValidating || isCommitting || uiBusy);
  }, [isValidating, isCommitting, uiBusy]);

  const updateState = useCallback((partial: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const reportUiBusy = useCallback((busy: boolean) => {
    setUiBusy(busy);
  }, []);

  const invalidateValidation = useCallback(() => {
    // Supersede any in-flight dry-run so a late response cannot clear stale.
    importRequestTokenRef.current += 1;
    setState((prev) => {
      if (!prev.importResult && !prev.validationStale) return prev;
      return { ...prev, validationStale: true };
    });
  }, []);

  const discardUnsavedProgress = useCallback(() => {
    // Clear storage first so an in-flight persist effect cannot resurrect
    // the previous CSV after navigation remounts the provider.
    clearWizardSession();
    setState((prev) => newImportWizardState(prev));
  }, []);

  // Seed the default page size from LaunchDarkly (clamped to MAX_PAGE_SIZE). The
  // per-user "Rows per page" selector overrides this within the session.
  const ldPageSize = useFeatureFlagNumber(
    NumberFeatureFlag.PayComponentImportPageSize,
  );
  useEffect(() => {
    setState((prev) => ({ ...prev, pageSize: clampPageSize(ldPageSize) }));
  }, [ldPageSize]);

  // After refresh, return to the step the user was on (#228). Redesign skips
  // resume so a refresh starts from a clean upload (Work Patterns parity).
  useEffect(() => {
    if (redesignEnabled || restoredRouteRef.current || !state.preview) return;
    restoredRouteRef.current = true;

    const session = loadWizardSession();
    if (!session || session.companyCode !== companyCode) return;

    const targetRoute = session.lastRoute;

    if (location.pathname !== targetRoute) {
      void navigate(targetRoute, { replace: true });
    }
  }, [
    companyCode,
    location.pathname,
    navigate,
    redesignEnabled,
    state.preview,
  ]);

  // Persist wizard progress on each meaningful change so refresh can resume (#228).
  // Redesign does not persist — refresh clears like Work Patterns.
  useEffect(() => {
    if (redesignEnabled) return;
    const record = buildWizardSessionRecord({
      companyCode,
      companyName,
      state,
      lastRoute: location.pathname,
    });
    if (record) {
      saveWizardSession(record);
    }
  }, [companyCode, companyName, location.pathname, redesignEnabled, state]);

  const resolveImportFile = useCallback((wizardState: WizardState): File | null => {
    if (wizardState.file) return wizardState.file;
    if (wizardState.csvText) return csvTextToFile(wizardState.csvText);
    return null;
  }, []);

  const runImport = useCallback(
    async (dryRun: boolean = false) => {
      const file = resolveImportFile(state);
      if (!file || state.mypayEnvironment === null) {
        return;
      }

      const requestToken = ++importRequestTokenRef.current;

      const variables = {
        file,
        mypayEnvironment: state.mypayEnvironment,
        ...(state.providerId !== null && state.providerId !== ""
          ? { providerId: state.providerId }
          : {}),
        ...(state.componentColumn !== null && state.componentColumn !== ""
          ? { componentColumn: state.componentColumn }
          : {}),
        // null = not chosen yet (omit → server reserved default);
        // "" = explicit none (still send so the server skips the reserved header).
        ...(state.startDateColumn !== null
          ? { startDateColumn: state.startDateColumn }
          : {}),
        ...(state.endDateColumn !== null
          ? { endDateColumn: state.endDateColumn }
          : {}),
      };

      const result = await mutateAsync(variables);

      // Ignore superseded dry-run / import responses after the user changed input.
      if (requestToken !== importRequestTokenRef.current) {
        return;
      }

      // Persist the result so screens can read it without re-fetching.
      // Fresh validation clears the "edited since last run" grouping.
      setState((prev) => ({
        ...prev,
        importResult: result,
        validationStale: false,
        editedRowIndexes: [],
      }));

      // Navigation:
      //  - needs_mapping → mapping screen (base path when redesign is on).
      //  - dry-run (valid or invalid) → the review screen (validate before commit).
      //  - real commit → results (committed) or the errors screen (invalid).
      if (result.status === "needs_mapping") {
        await navigate(mappingRoute);
      } else if (dryRun) {
        await navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/review`);
      } else if (result.status === "committed") {
        await navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/results`);
      } else if (result.status === "invalid") {
        await navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/errors`);
      } else {
        await navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/review`);
      }
    },
    [state, resolveImportFile, mutateAsync, navigate, mappingRoute],
  );

  return {
    state,
    setState,
    updateState,
    navigate,
    runImport,
    invalidateValidation,
    reportUiBusy,
    discardUnsavedProgress,
    isValidating,
    redesignEnabled,
    mappingRoute,
  };
}

/** Track 1 (flag off / default): invokeAgent Temporal workflow commit. */
function WizardProviderTrackOne(props: WizardProviderProps) {
  const { companyCode, companyName } = props;

  // Commit runs here (not on a screen) so it survives Review→Results navigation
  // and isn't double-invoked by StrictMode's mount effects. The Results screen
  // renders the live progress from `commitProgress` / `isCommitting`.
  const agentConfig = useCompanyProvisionAgentId();
  const { commit, progress: commitProgress, isCommitting } = useWorkflowCommit(
    companyCode,
    {
      agentId: agentConfig.agentId,
      serviceAccountId: agentConfig.serviceAccountId,
    },
  );

  const {
    state,
    setState,
    updateState,
    navigate,
    runImport,
    invalidateValidation,
    reportUiBusy,
    discardUnsavedProgress,
    isValidating,
    redesignEnabled,
    mappingRoute,
  } = useWizardProviderState(props, isCommitting);

  const [commitError, setCommitError] = useState<string | null>(null);

  const commitImport = useCallback(
    async (args: CommitImportArgs) => {
      setCommitError(null);
      // Clear the previous run's result so Results doesn't flash stale output.
      setState((prev) => ({
        ...prev,
        importResult: null,
        validationStale: false,
        editedRowIndexes: [],
      }));
      // Start the commit BEFORE navigating: the hook flips `isCommitting` true
      // (and resets progress) synchronously, so Results renders the fresh
      // loading view immediately rather than the prior result / stale progress.
      const pending = commit({
        csvText: args.csvText,
        providerId: args.providerId,
        componentColumn: args.componentColumn,
        startDateColumn: args.startDateColumn,
        endDateColumn: args.endDateColumn,
        mypayEnvironment: args.mypayEnvironment,
        mappings: args.mappings,
        componentResolutions: args.componentResolutions,
      });
      void navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/results`);
      try {
        const result = await pending;
        // Preserve failed source rows for the Results "Download errors" action:
        // use the pre-computed set (import-valid path) or derive from the result.
        const failedRowsCsv =
          args.failedRowsCsv ??
          (result.errors.length > 0
            ? buildFailedRowsCsv(args.csvText, result.errors)
            : null);
        setState((prev) => ({ ...prev, importResult: result, failedRowsCsv }));
        if (result.status === "committed") {
          clearWizardSession();
        }
        // A chunk can come back non-committed (needs_mapping / invalid); route to
        // the right screen rather than leaving the user on Results, which renders
        // a committed-success view. "committed" stays on Results.
        if (result.status === "needs_mapping") {
          await navigate(mappingRoute);
        } else if (result.status === "invalid") {
          await navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/errors`);
        }
      } catch (err) {
        setCommitError(
          err instanceof Error ? err.message : "An unexpected error occurred.",
        );
      }
    },
    [commit, mappingRoute, navigate, setState],
  );

  const contextValue = useMemo(
    () => ({
      companyCode,
      companyName,
      state,
      updateState,
      invalidateValidation,
      reportUiBusy,
      discardUnsavedProgress,
      runImport,
      commitImport,
      commitProgress,
      isCommitting,
      isValidating,
      commitError,
      redesignEnabled,
    }),
    [
      companyCode,
      companyName,
      state,
      updateState,
      invalidateValidation,
      reportUiBusy,
      discardUnsavedProgress,
      runImport,
      commitImport,
      commitProgress,
      isCommitting,
      isValidating,
      commitError,
      redesignEnabled,
    ],
  );

  return (
    <WizardContext.Provider value={contextValue}>
      <WizardRouteGuard state={state} isCommitting={isCommitting} />
    </WizardContext.Provider>
  );
}

/** Track 2 (flag on): Slice 5 workloads + entity REST APIs commit. */
function WizardProviderWorkloads(props: WizardProviderProps) {
  const { companyCode, companyName } = props;
  const manager = usePayComponentImportWorkloadsManager();
  const {
    commit: commitToWorkloads,
    result: workloadsResult,
    isCommitting,
    managerError,
  } = manager;

  const {
    state,
    setState,
    updateState,
    navigate,
    runImport,
    invalidateValidation,
    reportUiBusy,
    discardUnsavedProgress,
    isValidating,
    redesignEnabled,
  } = useWizardProviderState(props, isCommitting);

  /**
   * `manager.commit()` is fire-and-forget (see the hook's own doc comment) —
   * it has no promise to resolve with, so the CSV text / precomputed
   * `failedRowsCsv` that `commitImport` was called with are stashed here for
   * the result-bridging effect below to read back once the pipeline settles.
   */
  const lastCommitArgsRef = useRef<CommitImportArgs | null>(null);

  const commitImport = useCallback(
    (args: CommitImportArgs) => {
      lastCommitArgsRef.current = args;
      // Clear the previous run's result so Results doesn't flash stale output.
      setState((prev) => ({
        ...prev,
        importResult: null,
        validationStale: false,
        editedRowIndexes: [],
      }));
      // Slice 5 `PayComponentSourceImportAdapter` expects a JSON array of
      // ImportRow (same shape Track 1 posts inline) — not raw CSV. The API
      // router also rejects `text/csv` with 415.
      const rows = buildImportRows(args.csvText, {
        companyCode,
        componentColumn: args.componentColumn,
        startDateColumn: args.startDateColumn,
        endDateColumn: args.endDateColumn,
        componentResolutions: args.componentResolutions,
      }).map((row, idx) => ({ ...row, rowIndex: idx + 1 }));
      commitToWorkloads({
        file: rowsToJsonImportFile(rows, "pay-components.csv"),
        companyCode,
        mypayEnvironment: args.mypayEnvironment,
        providerId: args.providerId ?? undefined,
        componentResolutions: args.componentResolutions,
      });
      void navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/results`);
      return Promise.resolve();
    },
    [companyCode, commitToWorkloads, navigate, setState],
  );

  // Bridges the manager's reactive `result` onto `WizardState.importResult`
  // once the import → read → optional PATCH → validate → run pipeline settles.
  // Track 1's equivalent does this inline after `await commit(...)` resolves;
  // Track 2 has no promise to await, so this effect is the bridge.
  useEffect(() => {
    const result = workloadsResult;
    if (!result) return;
    const args = lastCommitArgsRef.current;
    setState((prev) => {
      if (prev.importResult === result) return prev;
      const failedRowsCsv =
        args?.failedRowsCsv ??
        (args && result.errors.length > 0
          ? buildFailedRowsCsv(args.csvText, result.errors)
          : null);
      return { ...prev, importResult: result, failedRowsCsv };
    });
    if (result.status === "committed") {
      clearWizardSession();
    } else if (result.status === "invalid") {
      void navigate(`${PAY_COMPONENT_IMPORT_BASE_PATH}/errors`);
    }
  }, [workloadsResult, navigate, setState]);

  const contextValue = useMemo(
    () => ({
      companyCode,
      companyName,
      state,
      updateState,
      invalidateValidation,
      reportUiBusy,
      discardUnsavedProgress,
      runImport,
      commitImport,
      commitProgress: null,
      isCommitting,
      isValidating,
      commitError: managerError,
      redesignEnabled,
    }),
    [
      companyCode,
      companyName,
      state,
      updateState,
      invalidateValidation,
      reportUiBusy,
      discardUnsavedProgress,
      runImport,
      commitImport,
      isCommitting,
      isValidating,
      managerError,
      redesignEnabled,
    ],
  );

  return (
    <WizardContext.Provider value={contextValue}>
      <WizardRouteGuard state={state} isCommitting={isCommitting} />
    </WizardContext.Provider>
  );
}
